import type { VoidComponent } from "solid-js";
import { For } from "solid-js";
import type { InferOutput } from "valibot";
import { array, nonEmpty, object, pipe, string } from "valibot";
import { keyFromSelector } from "i18next";
import { Checkbox as ArkCheckbox, Fieldset } from "@ark-ui/solid";
import type { TranslationKey } from "../../../../state/i18n";
import { useTranslation } from "../../../../state/i18n";
import type { GetErrorFn, UpdateValueFn } from "../../../../components/form/createForm";
import { errorTranslation, require, requireText } from "../../../../utils/validation/base";
import { MAX_CHARACTERS_LG } from "../../../../constants";
import { CheckboxIndicator } from "../../../../components/checkbox/Checkbox";
import { ErrorMessage, RequiredIndicator } from "../../../../components/inputs/Input";
import TextAreaInput from "../../../../components/inputs/TextAreaInput";
import Text from "../../../../components/text/Text";

const TERMINATION_REASONS: { label: TranslationKey; value: string }[] = [
  { label: keyFromSelector(($) => $.TERMINATION_FEEDBACK_REASON_LOW_VALUE), value: "LOW_VALUE" },
  { label: keyFromSelector(($) => $.TERMINATION_FEEDBACK_REASON_TOO_EXPENSIVE), value: "TOO_EXPENSIVE" },
  { label: keyFromSelector(($) => $.TERMINATION_FEEDBACK_REASON_PROJECT_BASED), value: "PROJECT_BASED" },
  { label: keyFromSelector(($) => $.TERMINATION_FEEDBACK_REASON_FOUND_ALTERNATIVE), value: "FOUND_ALTERNATIVE" },
  {
    label: keyFromSelector(($) => $.TERMINATION_FEEDBACK_REASON_INTERNAL_ALTERNATIVE),
    value: "INTERNAL_ALTERNATIVE",
  },
  { label: keyFromSelector(($) => $.TERMINATION_FEEDBACK_REASON_MISSING_FEATURE), value: "MISSING_FEATURE" },
  { label: keyFromSelector(($) => $.TERMINATION_FEEDBACK_REASON_TECHNICAL_ISSUES), value: "TECHNICAL_ISSUES" },
  { label: keyFromSelector(($) => $.TERMINATION_FEEDBACK_REASON_FINANCIAL_ISSUES), value: "FINANCIAL_ISSUES" },
  { label: keyFromSelector(($) => $.TERMINATION_FEEDBACK_REASON_OTHER), value: "OTHER" },
];

export const terminationFeedbackSchema = object({
  terminationReasons: require(
    pipe(array(string()), nonEmpty(errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY)))),
  ),
  message: requireText(MAX_CHARACTERS_LG, true),
});
export type TerminationFeedback = InferOutput<typeof terminationFeedbackSchema>;

type TerminationFeedbackFieldsProps = {
  updateValue: UpdateValueFn<TerminationFeedback>;
  error: GetErrorFn<TerminationFeedback>;
};

/** Asks why a customer is leaving, shared by deleting the account and cancelling the subscription */
const TerminationFeedbackFields: VoidComponent<TerminationFeedbackFieldsProps> = (props) => {
  const { t } = useTranslation();

  return (
    <div class="flex flex-col gap-6">
      <Fieldset.Root>
        <Fieldset.Legend class="text-base-semibold text-secondary">
          {t(($) => $.TERMINATION_FEEDBACK_REASONS_LABEL)}
          <RequiredIndicator required />
        </Fieldset.Legend>

        <ArkCheckbox.Group
          class="flex flex-col gap-4 pt-4"
          onValueChange={(value) => {
            props.updateValue("terminationReasons", value);
          }}
        >
          <For each={TERMINATION_REASONS}>
            {(item) => (
              <ArkCheckbox.Root value={item.value}>
                <div class="relative flex min-w-0 shrink gap-2">
                  <ArkCheckbox.HiddenInput class="peer" />
                  <CheckboxIndicator />
                  <ArkCheckbox.Label class="line-clamp-4 shrink text-base-regular text-secondary">
                    <Text text={t(item.label)} truncate />
                  </ArkCheckbox.Label>
                </div>
              </ArkCheckbox.Root>
            )}
          </For>
        </ArkCheckbox.Group>
        <div class="pt-1">
          <ErrorMessage errorMessage={props.error("terminationReasons")} />
        </div>
      </Fieldset.Root>

      <TextAreaInput
        label={t(($) => $.TERMINATION_FEEDBACK_MESSAGE_LABEL)}
        required
        maxLength={MAX_CHARACTERS_LG}
        errorMessage={props.error("message")}
        onInput={(e) => {
          props.updateValue("message", e.target.value);
        }}
        onBlur={(e) => {
          props.updateValue("message", e.target.value);
        }}
      />
    </div>
  );
};

export default TerminationFeedbackFields;
